import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineShieldCheck,
  HiOutlineFunnel,
  HiOutlineMagnifyingGlass,
  HiOutlineCalendar,
  HiOutlineEye,
  HiOutlineEllipsisVertical,
  HiOutlineXMark,
  HiOutlineChevronDown,
  HiOutlineEnvelope,
  HiOutlineLockClosed,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowLeft,
  HiOutlineNoSymbol,
  HiOutlinePhone,
  HiOutlineDocumentText,
  HiOutlineUser,
  HiOutlineMapPin,
  HiOutlineCalendarDays,
  HiOutlineExclamationTriangle,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { usePermissions, hasPermission } from "./permissions";
import { applicationsAPI, getToken } from "../services/api";
import toast from "react-hot-toast";
import BroadcastModal from "./BroadcastModal";
import "./AdminApplications.css";

const APP_STATS = [
  { label: "Total Applications", Icon: HiOutlineShieldCheck, color: "#e05a2b", bg: "rgba(var(--admin-rust-rgb), 0.1)", key: "total" },
  { label: "New Applications", Icon: HiOutlineClipboardDocumentList, color: "#e05a2b", bg: "rgba(var(--admin-rust-rgb), 0.1)", key: "new" },
  { label: "Accepted", Icon: HiOutlineCheckCircle, color: "#16a34a", bg: "rgba(22,163,74,0.1)", key: "accepted" },
  { label: "Rejected", Icon: HiOutlineXCircle, color: "#dc2626", bg: "rgba(220,38,38,0.1)", key: "rejected" },
  { label: "Banned", Icon: HiOutlineNoSymbol, color: "#1b1b1b", bg: "rgba(27,27,27,0.1)", key: "banned" },
];

const STATUS_CLASSES = {
  New: "abadge--new",
  Reviewing: "abadge--reviewing",
  "Under Review": "abadge--under-review",
  Accepted: "abadge--accepted",
  Rejected: "abadge--rejected",
  Banned: "abadge--banned",
};

const STATUSES = ["All Statuses", "New", "Reviewing", "Accepted", "Rejected", "Banned"];
const CATEGORIES = ["All Categories", "Veteran", "First Responder", "Law Enforcement", "Gold Star Family"];

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—");

const PRESCREENING_QUESTIONS = [
  { id: "current_state", section: "Where You Are Right Now", label: "In a few sentences, how would you describe where you're at emotionally and mentally right now?" },
  { id: "support_system", section: "Where You Are Right Now", label: "Do you currently have people in your life you can turn to for support?" },
  { id: "sleep_quality", section: "Where You Are Right Now", label: "How would you rate your sleep over the past 30 days?" },
  { id: "current_treatment", section: "Health & Safety", label: "Are you currently under the care of a therapist, counselor, or medical provider for mental health?" },
  { id: "medications", section: "Health & Safety", label: "Please list any medications you are currently taking." },
  { id: "physical_limitations", section: "Health & Safety", label: "Do you have any physical limitations, injuries, or medical conditions we should be aware of for retreat activities?" },
  { id: "expectations", section: "Your Readiness", label: "What are you hoping to get out of this retreat?" },
  { id: "commitment", section: "Your Readiness", label: "Are you able to fully commit to all 7 days of the retreat, including travel?" },
];

function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="aap__section">
      <button type="button" className="aap__section-btn" onClick={() => setOpen((v) => !v)}>
        <span>{title}</span>
        <HiOutlineChevronDown className="aap__section-chevron" style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
      </button>
      {open && <div className="aap__section-body">{children}</div>}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="aap__info-row">
      <span className="aap__info-label">{label}</span>
      <span className="aap__info-value">{value}</span>
    </div>
  );
}

function ContactModal({ app, onClose }) {
  if (!app) return null;
  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aap__contact-head">
          <h3>Contact Applicant</h3>
          <button type="button" onClick={onClose}>
            <HiOutlineXMark />
          </button>
        </div>
        <p className="aap__contact-name">{app.name || `${app.first_name} ${app.last_name}`}</p>

        {app.email && (
          <a href={`mailto:${app.email}?subject=Your Forge Retreat Application`} className="aap__contact-option">
            <div className="aap__contact-icon" style={{ background: "rgba(var(--admin-rust-rgb), 0.1)" }}>
              <HiOutlineEnvelope style={{ color: "#e05a2b" }} />
            </div>
            <div>
              <p className="aap__contact-type">Email</p>
              <p className="aap__contact-detail">{app.email}</p>
            </div>
          </a>
        )}
        {app.phone && (
          <a href={`tel:${app.phone}`} className="aap__contact-option">
            <div className="aap__contact-icon" style={{ background: "rgba(22,163,74,0.1)" }}>
              <HiOutlinePhone style={{ color: "#16a34a" }} />
            </div>
            <div>
              <p className="aap__contact-type">Phone</p>
              <p className="aap__contact-detail">{app.phone}</p>
            </div>
          </a>
        )}
        {!app.phone && !app.email && <p style={{ color: "#9ca3af", textAlign: "center", padding: "1rem" }}>No contact details available.</p>}
      </div>
    </div>
  );
}

function DateFilter({ dateFrom, dateTo, onChange, onClear }) {
  return (
    <div className="aap__date-picker">
      <div className="aap__date-field">
        <label>From</label>
        <input type="date" value={dateFrom} onChange={(e) => onChange("from", e.target.value)} />
      </div>
      <div className="aap__date-field">
        <label>To</label>
        <input type="date" value={dateTo} onChange={(e) => onChange("to", e.target.value)} />
      </div>
      <button type="button" className="a-btn a-btn--outline" onClick={onClear} style={{ fontSize: "0.75rem", padding: "0.35rem 0.6rem" }}>
        Clear
      </button>
    </div>
  );
}

/* ── Pipeline mini progress — used in the sidebar for at-a-glance status ── */
function PipelineMini({ app }) {
  const steps = [
    { key: "submitted", done: true },
    { key: "prescreening", done: !!app.prescreening_at },
    { key: "screening", done: Boolean(app.screening_cleared) },
    { key: "medical_clearance", done: !!app.medical_submitted_at },
    { key: "cleared", done: Boolean(app.medical_cleared) },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  return (
    <div className="aap__sidebar-pipeline">
      <div className="aap__sidebar-pipeline-dots">
        {steps.map((s, i) => (
          <span key={s.key} className={`aap__sidebar-pipeline-dot ${s.done ? "aap__sidebar-pipeline-dot--done" : ""}`} />
        ))}
      </div>
      <p className="aap__sidebar-pipeline-label">{doneCount === 5 ? "Fully Cleared" : `Step ${doneCount} of 5`}</p>
    </div>
  );
}

function ApplicationViewer({ app, onBack, onSave, onRefreshApp, saving }) {
  const { admin } = usePermissions();
  const canUpdateStatus = hasPermission(admin, "applications.update_status");
  const canAddNotes = hasPermission(admin, "applications.add_notes");
  const canClearance = hasPermission(admin, "applications.pipeline_clearance");
  const [status, setStatus] = useState(app?.status || "New");
  const [activeTab, setActiveTab] = useState("Overview");
  const [showContact, setShowContact] = useState(false);
  const [internalNotes, setInternalNotes] = useState(app?.internal_notes || app?.notes?.filter((n) => n.note_type === "internal") || []);
  const [applicantNotes, setApplicantNotes] = useState(app?.applicant_notes || app?.notes?.filter((n) => n.note_type === "applicant") || []);
  const [newInternal, setNewInternal] = useState("");
  const [newApplicant, setNewApplicant] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [pipelineBusy, setPipelineBusy] = useState(false);

  if (!app) return null;
  const name = app.name || `${app.first_name || ""} ${app.last_name || ""}`.trim();
  const totalNotes = internalNotes.length + applicantNotes.length;
  const replyAttachments = (app.applicant_notes || []).flatMap((n) => (n.replies || []).filter((r) => r.file_name));
  const totalDocCount = (app.documents?.length || 0) + replyAttachments.length;
  const TABS = ["Overview", `Documents (${totalDocCount})`, `Notes (${totalNotes})`];

  const flaggedSuicidal = app.eligibility_question_one === "yes";
  const flaggedAddiction = app.eligibility_question_two === "yes";
  const hasSafetyFlag = flaggedSuicidal || flaggedAddiction;

  const handleAddInternal = async () => {
    if (!newInternal.trim()) return;
    setAddingNote(true);
    try {
      const res = await applicationsAPI.addNote(app.id, newInternal.trim());
      const note = res?.data || { id: Date.now(), note: newInternal.trim(), admin_name: "You", created_at: new Date().toISOString(), note_type: "internal" };
      setInternalNotes((prev) => [note, ...prev]);
      setNewInternal("");
      toast.success("Internal note saved.");
    } catch (err) {
      toast.error("Failed to save note: " + err.message);
    } finally {
      setAddingNote(false);
    }
  };

  /* ── Pipeline action — fetches the fresh application after a successful update
       and pushes it back to the parent (onRefreshApp) so the dots/buttons update
       instantly, without needing a manual page reload. ── */
  const handlePipelineAction = async (appId, action) => {
    setPipelineBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/applications/${appId}/pipeline`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Pipeline updated.");
        try {
          const fresh = await applicationsAPI.getOne(appId);
          if (fresh?.data && onRefreshApp) {
            onRefreshApp(fresh.data);
          } else {
            onSave(appId, status);
          }
        } catch (_) {
          onSave(appId, status);
        }
      } else {
        toast.error(data.message || "Failed.");
      }
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setPipelineBusy(false);
    }
  };

  const handleAddApplicant = async () => {
    if (!newApplicant.trim()) return;
    setAddingNote(true);
    try {
      const res = await applicationsAPI.addApplicantNote(app.id, newApplicant.trim());
      const note = res?.data || { id: Date.now(), note: newApplicant.trim(), admin_name: "You", created_at: new Date().toISOString(), note_type: "applicant" };
      setApplicantNotes((prev) => [...prev, note]);
      setNewApplicant("");
      toast.success("Note sent to applicant by email.");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setAddingNote(false);
    }
  };

  return (
    <div className="aap__viewer">
      <div className="aap__viewer-bar">
        <button type="button" className="aap__back-btn" onClick={onBack}>
          <HiOutlineArrowLeft /> Back to Applications
        </button>
        <div className="aap__viewer-actions">
          {canUpdateStatus && (
            <div className="aap__viewer-actions-group">
              <select className="aap__viewer-status-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                {["New", "Reviewing", "Accepted", "Rejected", "Banned"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
              <button type="button" className="a-btn a-btn--rust" disabled={saving} onClick={() => onSave(app.id, status)}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          )}
          <button type="button" className="a-btn a-btn--outline" onClick={() => setShowContact(true)}>
            <HiOutlineEnvelope /> Contact
          </button>
        </div>
      </div>

      {/* Safety flag banner — always visible, never buried in an accordion */}
      {hasSafetyFlag && (
        <div className="aap__safety-banner">
          <HiOutlineExclamationTriangle className="aap__safety-banner-icon" />
          <div className="aap__safety-banner-body">
            <p className="aap__safety-banner-title">Safety Review Required</p>
            <p className="aap__safety-banner-text">
              {flaggedSuicidal && "This applicant indicated active suicidal thoughts on their eligibility screening. "}
              {flaggedAddiction && "This applicant indicated active addiction or substance use that may affect safe participation. "}
              Please review before proceeding with clearance.
            </p>
          </div>
        </div>
      )}

      <div className="aap__viewer-content">
        <div className="aap__viewer-main">
          <div className="acard aap__viewer-header">
            <div className="aap__viewer-avatar" style={{ background: "#b85c38" }}>
              {(app.first_name || name || "?")[0].toUpperCase()}
              {app.last_name ? app.last_name[0].toUpperCase() : ""}
            </div>
            <div className="aap__viewer-identity">
              <h2 className="aap__viewer-name">{name}</h2>
              <div className="aap__viewer-meta-row">
                <span className="aap__viewer-category">
                  <HiOutlineShieldCheck style={{ color: "#16a34a" }} /> {app.category}
                </span>
                <span className={`abadge ${STATUS_CLASSES[app.status] || ""}`}>{app.status}</span>
              </div>
              <div className="aap__viewer-contact-row">
                {app.email && (
                  <span>
                    <HiOutlineEnvelope />
                    {app.email}
                  </span>
                )}
                {app.phone && (
                  <span>
                    <HiOutlinePhone />
                    {app.phone}
                  </span>
                )}
                {(app.city || app.country) && (
                  <span>
                    <HiOutlineMapPin />
                    {[app.city, app.state, app.country].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
              <p className="aap__viewer-submitted">
                <HiOutlineCalendarDays /> Submitted {fmt(app.created_at || app.submitted)}
              </p>
            </div>
          </div>

          <div className="aap__viewer-tabs">
            {TABS.map((t) => (
              <button key={t} type="button" className={`aap__viewer-tab ${activeTab === t ? "aap__viewer-tab--active" : ""}`} onClick={() => setActiveTab(t)}>
                {t}
              </button>
            ))}
          </div>

          {activeTab === "Overview" && (
            <div className="aap__viewer-sections">
              {/* ── Workflow card — the active clearance process an admin acts on ── */}
              <div className="acard aap__workflow-card">
                <p className="aap__workflow-card-title">Clearance Workflow</p>

                <Section title="Application Pipeline" defaultOpen>
                  <div className="aap__pipeline">
                    {[
                      { key: "submitted", label: "Submitted", done: true },
                      { key: "prescreening", label: "Prescreening", done: !!app.prescreening_at },
                      { key: "screening", label: "Screening Cleared", done: !!app.screening_cleared },
                      { key: "medical_clearance", label: "Medical Submitted", done: !!app.medical_submitted_at },
                      { key: "cleared", label: "Medical Cleared", done: !!app.medical_cleared },
                    ].map((step) => (
                      <div key={step.key} className="aap__pipeline-step">
                        <div className={`aap__pipeline-dot ${step.done ? "aap__pipeline-dot--done" : ""}`}>{step.done ? <HiOutlineCheckCircle style={{ fontSize: "0.8rem" }} /> : null}</div>
                        <span className={`aap__pipeline-label ${step.done ? "aap__pipeline-label--done" : ""}`}>{step.label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="aap__pipeline-actions">
                    {!Boolean(app.screening_cleared) && (
                      <button type="button" className="a-btn a-btn--outline" style={{ fontSize: "0.78rem" }} disabled={pipelineBusy} onClick={() => handlePipelineAction(app.id, "clear_screening")}>
                        {pipelineBusy ? "Updating…" : "✓ Clear Screening"}
                      </button>
                    )}
                    {Boolean(app.screening_cleared) && !Boolean(app.medical_submitted_at) && !Boolean(app.medical_cleared) && (
                      <button type="button" className="a-btn a-btn--outline" style={{ fontSize: "0.78rem" }} disabled={pipelineBusy} onClick={() => handlePipelineAction(app.id, "submit_medical")}>
                        {pipelineBusy ? "Updating…" : "📋 Mark Medical Submitted"}
                      </button>
                    )}
                    {Boolean(app.screening_cleared) && Boolean(app.medical_submitted_at) && !Boolean(app.medical_cleared) && (
                      <button type="button" className="a-btn a-btn--outline" style={{ fontSize: "0.78rem" }} disabled={pipelineBusy} onClick={() => handlePipelineAction(app.id, "clear_medical")}>
                        {pipelineBusy ? "Updating…" : "✓ Clear Medical"}
                      </button>
                    )}
                    {Boolean(app.medical_cleared) && (
                      <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#16a34a", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                        <HiOutlineCheckCircle /> Fully Cleared
                      </span>
                    )}
                  </div>

                  {!canClearance && <p style={{ fontSize: "0.76rem", color: "#9ca3af", fontStyle: "italic", margin: 0 }}>You don't have permission to clear pipeline steps.</p>}
                </Section>

                <Section title={`Prescreening Responses ${app.prescreening_at ? "" : "(Not Submitted)"}`}>
                  {!app.prescreening_at ? (
                    <p className="aap__placeholder" style={{ padding: "1rem", textAlign: "center" }}>
                      Applicant hasn't completed the prescreening questionnaire yet.
                    </p>
                  ) : (
                    (() => {
                      let answers = {};
                      try {
                        answers = typeof app.prescreening_data === "string" ? JSON.parse(app.prescreening_data || "{}") : app.prescreening_data || {};
                      } catch (_) {
                        answers = {};
                      }

                      const sections = [...new Set(PRESCREENING_QUESTIONS.map((q) => q.section))];

                      return (
                        <div className="aap__prescreening">
                          <p className="aap__prescreening-date">Submitted {fmt(app.prescreening_at)}</p>
                          {sections.map((section) => (
                            <div key={section} className="aap__prescreening-section">
                              <p className="aap__prescreening-heading">{section}</p>
                              {PRESCREENING_QUESTIONS.filter((q) => q.section === section).map((q) => (
                                <div key={q.id} className="aap__prescreening-row">
                                  <p className="aap__prescreening-q">{q.label}</p>
                                  <p className="aap__prescreening-a">{answers[q.id] || "—"}</p>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  )}
                </Section>

                <Section title="Medical Clearance">
                  {!app.screening_cleared ? (
                    <p className="aap__placeholder" style={{ padding: "1rem", textAlign: "center" }}>
                      Available once screening is cleared.
                    </p>
                  ) : !app.medical_submitted_at ? (
                    <p className="aap__placeholder" style={{ padding: "1rem", textAlign: "center" }}>
                      Applicant hasn't submitted medical documents yet.
                    </p>
                  ) : (
                    <div className="aap__medical-status">
                      <p className="aap__medical-line">
                        <strong>Submitted:</strong> {fmt(app.medical_submitted_at)}
                      </p>
                      <p className="aap__medical-line">
                        <strong>Status:</strong>{" "}
                        {app.medical_cleared ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✓ Cleared</span> : <span style={{ color: "#d97706", fontWeight: 700 }}>Pending Review</span>}
                      </p>
                      <p className="aap__medical-hint">Documents are listed in the Documents tab, tagged "Medical Clearance".</p>
                    </div>
                  )}
                </Section>
              </div>

              {/* ── Reference data card — static application details, less frequently needed ── */}
              <div className="acard aap__reference-card">
                <p className="aap__workflow-card-title aap__workflow-card-title--muted">Application Details</p>

                <Section title="Personal Information" defaultOpen>
                  <InfoRow label="Full Name" value={name} />
                  <InfoRow label="Email" value={app.email} />
                  <InfoRow label="Phone" value={app.phone} />
                  <InfoRow label="Date of Birth" value={app.date_of_birth ? fmt(app.date_of_birth) : null} />
                  <InfoRow label="Address" value={app.address} />
                  <InfoRow label="City" value={app.city} />
                  <InfoRow label="State / Province" value={app.state} />
                  <InfoRow label="Country" value={app.country} />
                </Section>

                <Section title="Service Information">
                  <InfoRow label="Category" value={app.category} />
                  <InfoRow label="Years of Service" value={app.years_of_service} />
                </Section>

                <Section title="Eligibility Answers">
                  <div className="aap__eligibility">
                    <div className={`aap__elig-item ${flaggedSuicidal ? "aap__elig-item--yes" : ""}`}>
                      <p className="aap__elig-q">Are you currently experiencing active suicidal thoughts?</p>
                      <p className="aap__elig-a">{flaggedSuicidal ? "⚠ YES — Review Required" : "No"}</p>
                    </div>
                    <div className={`aap__elig-item ${flaggedAddiction ? "aap__elig-item--yes" : ""}`}>
                      <p className="aap__elig-q">Are you currently experiencing active addiction or substance use that would make participation unsafe?</p>
                      <p className="aap__elig-a">{flaggedAddiction ? "⚠ YES — Review Required" : "No"}</p>
                    </div>
                  </div>
                </Section>

                <Section title="Personal Story">
                  {app.story ? (
                    (() => {
                      const raw = app.story || "";
                      const sections = [
                        { key: "WHY I'M APPLYING:", label: "Why They're Applying" },
                        { key: "WHAT I HOPE TO GAIN:", label: "What They Hope to Gain" },
                        { key: "ADDITIONAL NOTES:", label: "Additional Notes" },
                      ];
                      let remaining = raw;
                      const parsed = [];
                      sections.forEach(({ key, label }) => {
                        const idx = remaining.indexOf(key);
                        if (idx !== -1) {
                          const after = remaining.slice(idx + key.length);
                          const nextKey = sections.find((s) => s.key !== key && after.indexOf(s.key) !== -1);
                          const end = nextKey ? after.indexOf(nextKey.key) : after.length;
                          parsed.push({ label, text: after.slice(0, end).trim() });
                          remaining = remaining.replace(remaining.slice(idx, idx + key.length + end), "");
                        }
                      });
                      if (parsed.length === 0) return <p className="aap__story-text">{raw}</p>;
                      return (
                        <div className="aap__story-sections">
                          {parsed.map(({ label, text }) =>
                            text ? (
                              <div key={label} className="aap__story-section">
                                <p className="aap__story-section-label">{label}</p>
                                <p className="aap__story-text">{text}</p>
                              </div>
                            ) : null,
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="aap__placeholder">No story provided.</p>
                  )}
                </Section>
              </div>
            </div>
          )}

          {activeTab.startsWith("Documents") && (
            <div className="aap__docs-wrap">
              <div className="acard aap__viewer-docs">
                <div className="aap__docs-section-head">
                  <p className="aap__docs-section-title">Application Documents</p>
                  <span className="aap__docs-section-badge">{app.documents?.length || 0}</span>
                </div>
                {(app.documents || []).length === 0 ? (
                  <p className="aap__placeholder" style={{ padding: "1.25rem", textAlign: "center" }}>
                    No documents submitted with application.
                  </p>
                ) : (
                  (app.documents || []).map((doc) => (
                    <div key={doc.id} className="aap__doc-card">
                      <div className="aap__doc-icon">
                        <HiOutlineDocumentText />
                      </div>
                      <div className="aap__doc-info">
                        <p className="aap__doc-name">{doc.file_name}</p>
                        <p className="aap__doc-type">
                          {doc.document_type} · Uploaded {fmt(doc.created_at)}
                        </p>
                      </div>
                      {doc.restricted ? (
                        <span className="aap__doc-type" title="Medical documents need the clinical permission on your role.">Clinical permission needed</span>
                      ) : (
                        <a href={applicationsAPI.documentUrl(app.id, doc.id)} target="_blank" rel="noreferrer" className="a-btn a-btn--outline" style={{ fontSize: "0.75rem" }}>
                          <HiOutlineEye /> View
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>

              <div className="acard aap__viewer-docs">
                <div className="aap__docs-section-head">
                  <p className="aap__docs-section-title">Message Attachments</p>
                  <span className="aap__docs-section-badge aap__docs-section-badge--rust">{replyAttachments.length}</span>
                </div>
                <p className="aap__docs-section-sub">Files the applicant uploaded when replying to notes from this panel.</p>
                {replyAttachments.length === 0 ? (
                  <p className="aap__placeholder" style={{ padding: "1.25rem", textAlign: "center" }}>
                    No message attachments yet.
                  </p>
                ) : (
                  (app.applicant_notes || [])
                    .flatMap((n) => (n.replies || []).filter((r) => r.file_name).map((r) => ({ ...r, noteContext: n.note })))
                    .map((r, i) => (
                      <div key={r.id || i} className="aap__doc-card">
                        <div className="aap__doc-icon aap__doc-icon--rust">
                          <HiOutlineDocumentText />
                        </div>
                        <div className="aap__doc-info">
                          <p className="aap__doc-name">{r.file_name}</p>
                          <p className="aap__doc-type">
                            Attached {fmt(r.created_at)}
                            {r.noteContext && (
                              <span className="aap__doc-note-ctx" title={r.noteContext}>
                                {" · "}In reply to: {r.noteContext.slice(0, 45)}
                                {r.noteContext.length > 45 ? "…" : ""}
                              </span>
                            )}
                          </p>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/applications/${app.id}/reply-documents/${r.id}?token=${getToken()}`}
                          target="_blank"
                          rel="noreferrer"
                          className="a-btn a-btn--outline"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <HiOutlineEye /> View
                        </a>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {activeTab.startsWith("Notes") && (
            <div className="aap__notes-dual">
              <div className="acard aap__notes-panel">
                <div className="aap__notes-panel-head">
                  <div className="aap__notes-panel-title">
                    <HiOutlineLockClosed className="aap__notes-panel-icon aap__notes-panel-icon--internal" />
                    <span>Internal Notes</span>
                  </div>
                  <span className="aap__notes-panel-badge aap__notes-panel-badge--internal">Admin only · Never shown to applicant</span>
                </div>

                {canAddNotes && (
                  <div className="aap__notes-compose">
                    <textarea
                      className="aap__notes-input"
                      rows={3}
                      placeholder="Add an internal note — only admins can see this…"
                      value={newInternal}
                      onChange={(e) => setNewInternal(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAddInternal();
                      }}
                    />
                    <div className="aap__notes-compose-footer">
                      <span className="aap__notes-hint">Ctrl+Enter to save</span>
                      <button type="button" className="a-btn a-btn--rust" onClick={handleAddInternal} disabled={addingNote || !newInternal.trim()}>
                        {addingNote ? "Saving…" : "Add Note"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="aap__notes-list">
                  {internalNotes.length === 0 ? (
                    <p className="aap__placeholder" style={{ padding: "1.25rem", textAlign: "center" }}>
                      No internal notes yet.
                    </p>
                  ) : (
                    internalNotes.map((n) => (
                      <div key={n.id} className="aap__note-card aap__note-card--internal">
                        <div className="aap__note-avatar aap__note-avatar--internal">
                          <HiOutlineUser />
                        </div>
                        <div className="aap__note-body">
                          <p className="aap__note-meta">
                            {n.admin_name || "Admin"} · {fmt(n.created_at)}
                          </p>
                          <p className="aap__note-text">{n.note}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="acard aap__notes-panel">
                <div className="aap__notes-panel-head">
                  <div className="aap__notes-panel-title">
                    <HiOutlineEnvelope className="aap__notes-panel-icon aap__notes-panel-icon--applicant" />
                    <span>Applicant Notes</span>
                  </div>
                  <span className="aap__notes-panel-badge aap__notes-panel-badge--applicant">Visible to applicant · Triggers email</span>
                </div>

                <div className="aap__notes-list">
                  {applicantNotes.length === 0 ? (
                    <p className="aap__placeholder" style={{ padding: "1.25rem", textAlign: "center" }}>
                      No applicant notes yet.
                    </p>
                  ) : (
                    applicantNotes.map((n) => (
                      <div key={n.id} className="aap__note-thread">
                        <div className="aap__note-card aap__note-card--applicant">
                          <div className="aap__note-avatar aap__note-avatar--applicant">
                            <HiOutlineEnvelope />
                          </div>
                          <div className="aap__note-body">
                            <p className="aap__note-meta">
                              {n.admin_name || "Admin"} · {fmt(n.created_at)}
                            </p>
                            <p className="aap__note-text">{n.note}</p>
                          </div>
                        </div>
                        {(n.replies || []).length > 0 && (
                          <div className="aap__note-replies">
                            {(n.replies || []).map((r, ri) => (
                              <div key={r.id || ri} className="aap__note-reply">
                                <div className="aap__note-reply-avatar">A</div>
                                <div className="aap__note-reply-body">
                                  <p className="aap__note-meta" style={{ color: "#b85c38" }}>
                                    Applicant replied · {fmt(r.created_at)}
                                  </p>
                                  {r.reply_text && <p className="aap__note-text">{r.reply_text}</p>}
                                  {r.file_name && r.id && (
                                    <a
                                      href={`${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/applications/${app.id}/reply-documents/${r.id}?token=${getToken()}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="aap__note-reply-file aap__note-reply-file--link"
                                    >
                                      <HiOutlineDocumentText />
                                      <span>{r.file_name}</span>
                                      <HiOutlineArrowTopRightOnSquare style={{ fontSize: "0.7rem", opacity: 0.7 }} />
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {(n.replies || []).length === 0 && <p className="aap__note-no-reply">No reply from applicant yet.</p>}
                      </div>
                    ))
                  )}
                </div>

                {canAddNotes && (
                  <div className="aap__notes-compose aap__notes-compose--applicant">
                    <div className="aap__notes-applicant-warn">
                      <HiOutlineEnvelope style={{ flexShrink: 0 }} />
                      This note will be <strong>emailed to {app.first_name || "the applicant"}</strong> immediately and saved permanently.
                    </div>
                    <textarea
                      className="aap__notes-input"
                      rows={3}
                      placeholder="Write a note for the applicant — they will receive this by email…"
                      value={newApplicant}
                      onChange={(e) => setNewApplicant(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAddApplicant();
                      }}
                    />
                    <div className="aap__notes-compose-footer">
                      <span className="aap__notes-hint">Ctrl+Enter to send</span>
                      <button type="button" className="a-btn a-btn--rust" onClick={handleAddApplicant} disabled={addingNote || !newApplicant.trim()}>
                        <HiOutlineEnvelope /> {addingNote ? "Sending…" : "Send to Applicant"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="aap__viewer-sidebar">
          <div className="acard">
            <p className="aap__status-label" style={{ marginBottom: "0.5rem" }}>
              Application Status
            </p>
            {canUpdateStatus ? (
              <>
                <select className="aap__select" style={{ width: "100%", marginBottom: "1rem" }} value={status} onChange={(e) => setStatus(e.target.value)}>
                  {["New", "Reviewing", "Accepted", "Rejected", "Banned"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <button type="button" className="a-btn a-btn--rust" style={{ width: "100%", marginBottom: "0.5rem" }} disabled={saving} onClick={() => onSave(app.id, status)}>
                  {saving ? "Saving…" : "Save Status"}
                </button>
              </>
            ) : (
              <span className={`abadge ${STATUS_CLASSES[app.status] || ""}`} style={{ display: "inline-block", marginBottom: "1rem" }}>
                {app.status}
              </span>
            )}

            {/* At-a-glance pipeline progress — no tab switch required */}
            <PipelineMini app={app} />

            {canUpdateStatus &&
              (app.status !== "Banned" ? (
                <button
                  type="button"
                  className="aap__ban-btn"
                  disabled={saving}
                  onClick={() => {
                    toast(
                      (t) => (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                          <p style={{ margin: 0, fontWeight: 700, color: "#1b1b1b" }}>Ban this applicant?</p>
                          <p style={{ margin: 0, fontSize: "0.78rem", color: "#6b6258", lineHeight: 1.5 }}>
                            This will permanently ban <strong>{app.first_name}</strong> from submitting any future applications.
                          </p>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              onClick={() => {
                                toast.dismiss(t.id);
                                onSave(app.id, "Banned");
                              }}
                              style={{ background: "#1b1b1b", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}
                            >
                              Ban Permanently
                            </button>
                            <button
                              onClick={() => toast.dismiss(t.id)}
                              style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ),
                      { duration: Infinity, style: { maxWidth: "320px" } },
                    );
                  }}
                >
                  <HiOutlineNoSymbol /> Ban Applicant
                </button>
              ) : (
                <button type="button" className="aap__unban-btn" disabled={saving} onClick={() => onSave(app.id, "Rejected")}>
                  <HiOutlineNoSymbol /> Lift Ban
                </button>
              ))}

            <button type="button" className="a-btn a-btn--outline" style={{ width: "100%", marginTop: "0.5rem" }} onClick={() => setShowContact(true)}>
              <HiOutlineEnvelope /> Contact Applicant
            </button>
          </div>

          <div className="acard aap__viewer-quick">
            <p className="aap__status-label">Quick Info</p>
            <div className="aap__quick-info-rows">
              <InfoRow label="Category" value={app.category} />
              <InfoRow label="Years of Service" value={app.years_of_service} />
              <InfoRow label="Country" value={app.country} />
              <InfoRow label="App ID" value={`#${app.id}`} />
            </div>
          </div>
        </div>
      </div>

      {showContact && <ContactModal app={app} onClose={() => setShowContact(false)} />}
    </div>
  );
}

export default function AdminApplications() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatus] = useState("All Statuses");
  const [catFilter, setCat] = useState("All Categories");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewApp, setViewApp] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [page, setPage] = useState(1);
  const [apps, setApps] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statsKpis, setStatsKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [searchParams] = useSearchParams();

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) params.search = search;
      if (statusFilter !== "All Statuses") params.status = statusFilter;
      if (catFilter !== "All Categories") params.category = catFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await applicationsAPI.list(params);
      setApps(res.data || []);
      setTotalCount(res.pagination?.total || 0);
      if (res.kpis) setStatsKpis(res.kpis);
      setChecked(new Set());
      setSelectAll(false);
    } catch (err) {
      console.error("Failed to load applications:", err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, catFilter, dateFrom, dateTo, page]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const handleSelect = async (app) => {
    setSelected(app);
    try {
      const res = await applicationsAPI.getOne(app.id);
      setViewApp(res.data || app);
    } catch {
      setViewApp(app);
    }
  };

  // Deep link: /admin/applications?id=123 opens that application directly
  // (used from Bookings, Queue and anywhere else that references a person).
  useEffect(() => {
    const id = Number(searchParams.get("id"));
    if (id) handleSelect({ id });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelectAll = () => {
    if (selectAll) {
      setChecked(new Set());
      setSelectAll(false);
    } else {
      setChecked(new Set(apps.map((a) => a.id)));
      setSelectAll(true);
    }
  };

  const toggleCheckApp = (id) => {
    setChecked((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      setSelectAll(n.size === apps.length);
      return n;
    });
  };

  const handleBulkStatus = async (newStatus) => {
    if (!newStatus || checked.size === 0) return;
    setSaving(true);
    try {
      await Promise.all([...checked].map((id) => applicationsAPI.update(id, { status: newStatus })));
      toast.success(`Updated ${checked.size} application${checked.size > 1 ? "s" : ""} to "${newStatus}".`);
      setChecked(new Set());
      setSelectAll(false);
      fetchApps();
    } catch (err) {
      toast.error("Bulk update failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (appId, newStatus, notes) => {
    setSaving(true);
    try {
      await applicationsAPI.update(appId, { status: newStatus, admin_notes: notes });
      fetchApps();
      setViewApp((prev) => (prev ? { ...prev, status: newStatus, admin_notes: notes } : prev));
      toast.success("Application updated successfully.");
    } catch (err) {
      toast.error("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const kpis = {
    total: totalCount,
    new: apps.filter((a) => a.status === "New").length,
    reviewing: apps.filter((a) => a.status === "Reviewing" || a.status === "Under Review").length,
    accepted: apps.filter((a) => a.status === "Accepted").length,
    rejected: apps.filter((a) => a.status === "Rejected").length,
    banned: apps.filter((a) => a.status === "Banned").length,
    ...statsKpis,
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / 10));

  if (viewApp) {
    return (
      <AdminLayout pageTitle="" pageSubtitle="" permission="applications.view">
        <ApplicationViewer
          app={viewApp}
          onBack={() => {
            setViewApp(null);
            setSelected(null);
          }}
          onSave={handleSave}
          onRefreshApp={(freshApp) => setViewApp(freshApp)}
          saving={saving}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Applications" pageSubtitle="Review and manage all retreat applications." permission="applications.view">
      <div className="aap__stats-row">
        {APP_STATS.map((s) => (
          <div key={s.key} className="acard aap__stat-card">
            <div className="aap__stat-top">
              <div className="aap__stat-icon" style={{ background: s.bg }}>
                <s.Icon style={{ color: s.color, fontSize: "1.1rem" }} />
              </div>
              <p className="aap__stat-label">{s.label}</p>
            </div>
            <p className="aap__stat-value">{kpis[s.key] ?? "—"}</p>
            <p className="aap__stat-change up">
              <HiOutlineArrowTrendingUp />
              Live count
            </p>
          </div>
        ))}
      </div>

      <div className="aap__filters">
        <div className="aap__search-wrap">
          <HiOutlineMagnifyingGlass className="aap__search-icon" />
          <input
            type="text"
            className="aap__search"
            placeholder="Search applicants..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="aap__select-wrap">
          <select
            className="aap__filter-select"
            value={statusFilter}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <HiOutlineChevronDown className="aap__select-icon" />
        </div>

        <div className="aap__select-wrap">
          <select
            className="aap__filter-select"
            value={catFilter}
            onChange={(e) => {
              setCat(e.target.value);
              setPage(1);
            }}
          >
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <HiOutlineChevronDown className="aap__select-icon" />
        </div>

        <div className="aap__date-wrap">
          <button type="button" className="aap__date-filter" onClick={() => setShowDatePicker((v) => !v)}>
            <HiOutlineCalendar className="aap__date-icon" />
            <span>{dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : dateFrom ? `From ${dateFrom}` : "All Dates"}</span>
            <HiOutlineChevronDown />
          </button>
          {showDatePicker && (
            <div className="aap__date-dropdown">
              <DateFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={(side, val) => {
                  side === "from" ? setDateFrom(val) : setDateTo(val);
                  setPage(1);
                }}
                onClear={() => {
                  setDateFrom("");
                  setDateTo("");
                  setPage(1);
                  setShowDatePicker(false);
                }}
              />
            </div>
          )}
        </div>

        <button type="button" className="a-btn a-btn--outline aap__email-btn" onClick={() => setShowBroadcast(true)} title="Send email to applicants">
          <HiOutlineEnvelope /> Email Applicants
        </button>

        <div className="aap__filter-extra-wrap">
          <button type="button" className={`a-btn ${showFilters ? "a-btn--rust" : "a-btn--outline"} aap__filter-btn`} onClick={() => setShowFilters((v) => !v)}>
            <HiOutlineFunnel /> Filters {showFilters ? "▲" : "▼"}
          </button>
          {showFilters && (
            <div className="aap__extra-filters">
              <p className="aap__extra-title">Advanced Filters</p>
              <div className="aap__extra-row">
                <label>Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                >
                  {STATUSES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="aap__extra-row">
                <label>Category</label>
                <select
                  value={catFilter}
                  onChange={(e) => {
                    setCat(e.target.value);
                    setPage(1);
                  }}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="a-btn a-btn--outline"
                style={{ width: "100%", marginTop: "0.5rem", fontSize: "0.75rem" }}
                onClick={() => {
                  setStatus("All Statuses");
                  setCat("All Categories");
                  setDateFrom("");
                  setDateTo("");
                  setPage(1);
                  setShowFilters(false);
                }}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {checked.size > 0 && (
        <div className="abulk__bar">
          <span className="abulk__count">
            {checked.size} application{checked.size > 1 ? "s" : ""} selected
          </span>
          <span className="abulk__label">Change status to:</span>
          {["Reviewing", "Accepted", "Rejected", "Banned"].map((st) => (
            <button
              key={st}
              type="button"
              className={`abulk__status-btn abulk__status-btn--${st.toLowerCase().replace(/ /g, "-")}`}
              disabled={saving}
              onClick={() => {
                if (window.confirm(`Set ${checked.size} application(s) to "${st}"?`)) handleBulkStatus(st);
              }}
            >
              {st}
            </button>
          ))}
          <button
            type="button"
            className="a-btn a-btn--ghost abulk__btn"
            onClick={() => {
              setChecked(new Set());
              setSelectAll(false);
            }}
          >
            Clear
          </button>
        </div>
      )}

      <div className="acard aap__table-card">
        {/* Desktop/tablet table view */}
        <div className="aap__table-scroll">
          <table className="aap__table">
            <thead>
              <tr>
                <th className="aap__th-check">
                  <input type="checkbox" className="aap__checkbox" checked={selectAll} onChange={toggleSelectAll} />
                </th>
                <th>Applicant</th>
                <th>Category</th>
                <th>Status</th>
                <th>Submitted On</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
                    Loading applications…
                  </td>
                </tr>
              ) : apps.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
                    No applications found.
                  </td>
                </tr>
              ) : (
                apps.map((app) => (
                  <tr key={app.id} className={`aap__row ${selected?.id === app.id ? "aap__row--selected" : ""}`} onClick={() => handleSelect(app)}>
                    <td className="aap__td-check" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="aap__checkbox" checked={checked.has(app.id)} onChange={() => toggleCheckApp(app.id)} />
                    </td>
                    <td>
                      <div className="aap__applicant">
                        <div className="aap__applicant-avatar" style={{ background: "#b85c38" }}>
                          {(app.first_name || app.name || "?")[0].toUpperCase()}
                          {app.last_name ? app.last_name[0].toUpperCase() : ""}
                        </div>
                        <div>
                          <p className="aap__applicant-name">{app.name || `${app.first_name} ${app.last_name}`}</p>
                          <p className="aap__applicant-email">{app.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="aap__category">
                        <HiOutlineShieldCheck className="aap__cat-icon" />
                        <span>{app.category}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`abadge ${STATUS_CLASSES[app.status] || ""}`}>{app.status}</span>
                    </td>
                    <td>
                      <div className="aap__submitted">
                        <p>{fmt(app.created_at || app.submitted)}</p>
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="aap__row-actions">
                        <button type="button" className="a-btn a-btn--ghost aap__action-btn" title="View application" onClick={() => handleSelect(app)}>
                          <HiOutlineEye />
                        </button>
                        <button
                          type="button"
                          className="a-btn a-btn--ghost aap__action-btn"
                          title="More options"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelect(app);
                          }}
                        >
                          <HiOutlineEllipsisVertical />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list — replaces the horizontally-scrolling table below 767px */}
        <div className="aap__mobile-list">
          {loading ? (
            <p className="aap__placeholder" style={{ padding: "2.5rem 0" }}>
              Loading applications…
            </p>
          ) : apps.length === 0 ? (
            <p className="aap__placeholder" style={{ padding: "2.5rem 0" }}>
              No applications found.
            </p>
          ) : (
            <>
              <label className="aap__mobile-selectall">
                <input type="checkbox" className="aap__checkbox" checked={selectAll} onChange={toggleSelectAll} />
                Select all on this page
              </label>
              {apps.map((app) => (
                <div key={app.id} className={`aap__mobile-card ${selected?.id === app.id ? "aap__mobile-card--selected" : ""}`} onClick={() => handleSelect(app)}>
                  <div className="aap__mobile-card-top" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="aap__checkbox" checked={checked.has(app.id)} onChange={() => toggleCheckApp(app.id)} />
                  </div>
                  <div className="aap__mobile-card-main">
                    <div className="aap__applicant-avatar" style={{ background: "#b85c38", width: "2.5rem", height: "2.5rem", fontSize: "0.75rem" }}>
                      {(app.first_name || app.name || "?")[0].toUpperCase()}
                      {app.last_name ? app.last_name[0].toUpperCase() : ""}
                    </div>
                    <div className="aap__mobile-card-info">
                      <p className="aap__applicant-name">{app.name || `${app.first_name} ${app.last_name}`}</p>
                      <p className="aap__applicant-email">{app.email}</p>
                    </div>
                    <span className={`abadge ${STATUS_CLASSES[app.status] || ""}`}>{app.status}</span>
                  </div>
                  <div className="aap__mobile-card-meta">
                    <span className="aap__category">
                      <HiOutlineShieldCheck className="aap__cat-icon" />
                      {app.category}
                    </span>
                    <span className="aap__mobile-card-date">{fmt(app.created_at || app.submitted)}</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="aap__pagination">
          <p className="aap__pagination-info">{totalCount > 0 ? `Showing ${(page - 1) * 10 + 1}–${Math.min(page * 10, totalCount)} of ${totalCount} results` : "No results"}</p>
          <div className="aap__pagination-pages">
            <button type="button" className="aap__page-btn" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <HiOutlineChevronLeft />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((p) => (
              <button key={p} type="button" className={`aap__page-btn ${page === p ? "aap__page-btn--active" : ""}`} onClick={() => setPage(p)}>
                {p}
              </button>
            ))}
            {totalPages > 5 && <span className="aap__page-btn">…</span>}
            {totalPages > 5 && (
              <button type="button" className={`aap__page-btn ${page === totalPages ? "aap__page-btn--active" : ""}`} onClick={() => setPage(totalPages)}>
                {totalPages}
              </button>
            )}
            <button type="button" className="aap__page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              <HiOutlineChevronRight />
            </button>
          </div>
          <div className="aap__select-wrap">
            <select className="aap__page-size">
              <option>10 / page</option>
              <option>20 / page</option>
              <option>50 / page</option>
            </select>
            <HiOutlineChevronDown className="aap__select-icon" />
          </div>
        </div>
      </div>
      {showBroadcast && (
        <BroadcastModal
          recipientType="applicants"
          selectedIds={[...checked]}
          totalCount={totalCount}
          onSend={(payload) => applicationsAPI.broadcast(payload)}
          onClose={() => setShowBroadcast(false)}
        />
      )}
    </AdminLayout>
  );
}
